import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Enums, Tables } from "@how/database";
import { supabase } from "../../lib/supabase";

/**
 * Plans: Core, Pro and Elite (ADR 0055). What each plan includes is data DCX edits here, never code: the three plan
 * tables are read by platform members and written by super-admin functions that audit every change.
 */
export type PlanKey = Enums<"plan_key">;
export type Plan = Pick<
  Tables<"platform_plans">,
  "key" | "name" | "description" | "sort_order" | "monthly_price_cents" | "theme_access" | "support"
>;
export type PlanModule = Pick<Tables<"platform_plan_modules">, "plan_key" | "module_key">;
export type ModulePrice = Pick<
  Tables<"platform_module_prices">,
  "module_key" | "selling_model" | "monthly_price_cents" | "note"
>;
export type SellingModel = "bundled" | "add_on" | "usage";
export type ThemeAccess = "included" | "premium";

export const PLAN_KEYS: readonly PlanKey[] = ["core", "pro", "elite"];
export const PLAN_LABEL: Readonly<Record<PlanKey, string>> = {
  core: "Core",
  pro: "Pro",
  elite: "Elite",
};
export const SELLING_MODEL_LABEL: Readonly<Record<SellingModel, string>> = {
  bundled: "Bundled with a plan",
  add_on: "Add-on",
  usage: "By usage",
};
export const THEME_ACCESS_LABEL: Readonly<Record<ThemeAccess, string>> = {
  included: "Included themes",
  premium: "Included and premium themes",
};

export type PlanCatalogModule = Pick<
  Tables<"platform_modules">,
  "key" | "display_name" | "status" | "is_base" | "sort_order"
>;

/**
 * Every module in the catalog, planned ones included, in catalog order. The Plans page lists them all: a plan may
 * include a module that is not offered to churches yet (the tick waits), and saving a plan sends the whole set, so a
 * module the page did not show would be dropped from the plan.
 */
export function useAllModules() {
  return useQuery({
    queryKey: ["module-catalog-all"],
    queryFn: async (): Promise<PlanCatalogModule[]> => {
      const { data, error } = await supabase
        .from("platform_modules")
        .select("key, display_name, status, is_base, sort_order")
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });
}

/** The three plans, in order. */
export function usePlans() {
  return useQuery({
    queryKey: ["platform-plans"],
    queryFn: async (): Promise<Plan[]> => {
      const { data, error } = await supabase
        .from("platform_plans")
        .select("key, name, description, sort_order, monthly_price_cents, theme_access, support")
        .order("sort_order");
      if (error) throw error;
      return data;
    },
  });
}

/** Which add-on modules each plan includes. */
export function usePlanModules() {
  return useQuery({
    queryKey: ["platform-plan-modules"],
    queryFn: async (): Promise<PlanModule[]> => {
      const { data, error } = await supabase
        .from("platform_plan_modules")
        .select("plan_key, module_key");
      if (error) throw error;
      return data;
    },
  });
}

/** How each add-on is sold on its own. */
export function useModulePrices() {
  return useQuery({
    queryKey: ["platform-module-prices"],
    queryFn: async (): Promise<ModulePrice[]> => {
      const { data, error } = await supabase
        .from("platform_module_prices")
        .select("module_key, selling_model, monthly_price_cents, note");
      if (error) throw error;
      return data;
    },
  });
}

/** How many churches are on each plan: what editing a plan affects. */
export function useChurchesByPlan() {
  return useQuery({
    queryKey: ["churches-by-plan"],
    queryFn: async (): Promise<Record<PlanKey, number>> => {
      const { data, error } = await supabase.from("tenants").select("plan");
      if (error) throw error;
      const counts: Record<PlanKey, number> = { core: 0, pro: 0, elite: 0 };
      for (const row of data) counts[row.plan] += 1;
      return counts;
    },
  });
}

/** The modules a plan includes, as a set keyed by plan. */
export function modulesByPlan(rows: readonly PlanModule[]): Record<PlanKey, Set<string>> {
  const out: Record<PlanKey, Set<string>> = { core: new Set(), pro: new Set(), elite: new Set() };
  for (const row of rows) out[row.plan_key].add(row.module_key);
  return out;
}

export interface PlanEdit {
  readonly plan: PlanKey;
  readonly name: string;
  readonly description: string;
  readonly monthlyPriceCents: number | null;
  readonly themeAccess: ThemeAccess;
  readonly support: string;
  readonly moduleKeys: readonly string[];
}

function invalidatePlans(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["platform-plans"] }),
    queryClient.invalidateQueries({ queryKey: ["platform-plan-modules"] }),
    queryClient.invalidateQueries({ queryKey: ["platform-module-prices"] }),
  ]);
}

/** Saves a plan's words, price, theme access and modules: set_plan then set_plan_modules, both audited. */
export function useSavePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (edit: PlanEdit) => {
      const plan = await supabase.rpc("set_plan", {
        p_plan: edit.plan,
        p_name: edit.name,
        p_description: edit.description,
        ...(edit.monthlyPriceCents === null
          ? {}
          : { p_monthly_price_cents: edit.monthlyPriceCents }),
        p_theme_access: edit.themeAccess,
        p_support: edit.support,
      });
      if (plan.error) throw plan.error;
      const modules = await supabase.rpc("set_plan_modules", {
        p_plan: edit.plan,
        p_module_keys: [...edit.moduleKeys],
      });
      if (modules.error) throw modules.error;
    },
    onSettled: () => invalidatePlans(queryClient),
  });
}

export interface PriceEdit {
  readonly moduleKey: string;
  readonly sellingModel: SellingModel;
  readonly monthlyPriceCents: number | null;
  readonly note: string;
}

export function useSaveModulePrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (edit: PriceEdit) => {
      const { error } = await supabase.rpc("set_module_price", {
        p_module_key: edit.moduleKey,
        p_selling_model: edit.sellingModel,
        ...(edit.monthlyPriceCents === null
          ? {}
          : { p_monthly_price_cents: edit.monthlyPriceCents }),
        p_note: edit.note,
      });
      if (error) throw error;
    },
    onSettled: () => invalidatePlans(queryClient),
  });
}

/** Moves a church to a plan (set_tenant_plan, super admin only, audited). */
export function useSetTenantPlan(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (plan: PlanKey) => {
      const { error } = await supabase.rpc("set_tenant_plan", {
        p_tenant_id: tenantId,
        p_plan: plan,
      });
      if (error) throw error;
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] }),
        queryClient.invalidateQueries({ queryKey: ["churches-by-plan"] }),
      ]),
  });
}

/** What was refused, in the words a platform admin reads. */
export function describePlanError(error: unknown): string {
  const e = (error ?? {}) as { code?: string; message?: string };
  if (e.code === "42501") return "Only a platform super admin can change plans.";
  if (e.code === "P0002") return "That plan or module is not in the catalog.";
  if (e.code === "23514") return e.message ?? "That change is not allowed.";
  return "The change was not saved. Try again.";
}

/** A price in cents as the control plane shows it; null reads as not set. */
export function formatPrice(cents: number | null): string {
  if (cents === null) return "Not set";
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} / month`;
}
